import { Link, NavLink, useLocation } from "react-router";
import { useSending } from "../sending/SendingContext";
import { sendingNavDot } from "../sending/sendingProtection";
import { nextSetupPath, setupCount, useSetup } from "../setup/SetupContext";
import { BrandGlyph, BrandWord } from "./BrandMark";
import { FlashsuiteMenu } from "./FlashsuiteMenu";
import { NAV_ITEMS } from "./navItems";
import { TABS, tabOf, useAppTabs } from "./phone/TabBar";
import { useUnreadCount } from "./phone/unread";
import { type SeasonLayout, useLayout } from "./season/layout";
import { cx } from "./ui/cx";
import { Icon } from "./ui/Icon";

/**
 * The operator shell's top bar. A computer keeps its bar (`.top`); the iPad gets the Season bar (`.tbar`): the brand, the
 * five places as tabs and Flashsuite's menu. A phone has the tab bar along the bottom instead.
 */
export function TopBar() {
  const layout = useLayout();
  if (layout === "phone") return null;
  return layout === "computer" ? <ComputerBar /> : <TabletBar layout={layout} />;
}

function SetupPill() {
  const setup = useSetup();
  const done = setup.state ? setupCount(setup.state) : 4;
  if (!setup.canEdit || !setup.state || done >= 4) return null;
  return (
    <Link className="setpill" to={nextSetupPath(setup.state)} title="Finish setting up">
      <span className="pd" aria-hidden="true">
        {[0, 1, 2, 3].map((i) => (
          <i key={i} className={i < done ? "on" : ""} />
        ))}
      </span>
      Setup<span className="lbt"> · {done} of 4 done</span>
    </Link>
  );
}

/**
 * The computer's top bar (`.top`): the brand, the places in the middle, Flashsuite's menu at the right. The tabs drop
 * their icons, then their words, before the bar can run past the window.
 */
function ComputerBar() {
  const { status } = useSending();
  const unread = useUnreadCount(true);
  const navDot = status ? sendingNavDot(status.state) : null;

  return (
    <header className="top">
      <NavLink to="/" end className="brand" aria-label="Flashroom home">
        <BrandGlyph size={30} />
        <BrandWord />
      </NavLink>

      <nav className="tabs" aria-label="Main">
        {NAV_ITEMS.map(({ to, label, icon, end }) => (
          <NavLink key={to} to={to} end={end} title={label} className={({ isActive }) => cx("tab", isActive && "on")}>
            <Icon name={icon} />
            <span className="tl">{label}</span>
            {to === "/settings" && navDot ? <span className={`navdot ${navDot}`} aria-label="Sending needs a look" /> : null}
            {to === "/conversations" && unread ? (
              <span className="badge" aria-label={`${unread} unread`}>
                {unread > 99 ? "99+" : unread}
              </span>
            ) : null}
          </NavLink>
        ))}
      </nav>

      <div className="topr">
        <SetupPill />
        <FlashsuiteMenu />
      </div>
    </header>
  );
}

/** The iPad's top bar (`.tbar`): the same five places as the phone's tab bar, as a row of tabs. */
function TabletBar({ layout }: { layout: SeasonLayout }) {
  const { pathname } = useLocation();
  const { status } = useSending();
  const unread = useUnreadCount(true);
  const navDot = status ? sendingNavDot(status.state) : null;
  const here = tabOf(pathname);
  const { lit, pressed } = useAppTabs();

  return (
    <header className="tbar" data-lay={layout}>
      <NavLink to="/" end className="tbar-brand" aria-label="Flashroom home">
        <BrandGlyph size={30} />
        <BrandWord />
      </NavLink>

      <nav className="tbar-tabs" aria-label="Main">
        {TABS.map(({ id, to, label, icon, end }) => (
          <NavLink key={to} to={to} end={end} onClick={pressed(id)} className={cx("tbar-tab", (lit ?? here) === id && "on")}>
            <Icon name={icon} />
            <span>{label}</span>
            {id === "conversations" && unread ? (
              <span className="badge" aria-label={`${unread} unread`}>
                {unread > 99 ? "99+" : unread}
              </span>
            ) : null}
            {id === "more" && navDot ? <span className={`navdot ${navDot}`} aria-label="Sending needs a look" /> : null}
          </NavLink>
        ))}
      </nav>

      <div className="topr">
        <SetupPill />
        <FlashsuiteMenu compact={layout === "upright"} />
      </div>
    </header>
  );
}
